class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    if (cartLink.dataset.pntCartDrawerBound === 'true') return;
    cartLink.dataset.pntCartDrawerBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      if (window.cartDrawerRefresh) window.cartDrawerRefresh(true);
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function bindHeaderCartDrawerFallback(root = document) {
  const cartLink = root.querySelector('#cart-icon-bubble') || document.querySelector('#cart-icon-bubble');
  const cartDrawer = document.querySelector('cart-drawer');

  if (!cartLink || !cartDrawer || cartLink.dataset.pntCartDrawerFallbackBound === 'true') return;

  cartLink.dataset.pntCartDrawerFallbackBound = 'true';
  cartLink.setAttribute('role', 'button');
  cartLink.setAttribute('aria-haspopup', 'dialog');

  cartLink.addEventListener('click', (event) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    event.preventDefault();
    drawer.open(cartLink);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bindHeaderCartDrawerFallback());
} else {
  bindHeaderCartDrawerFallback();
}

document.addEventListener('shopify:section:load', (event) => bindHeaderCartDrawerFallback(event.target));


/* ===============================
   Cart Drawer Recommendations
================================ */

(function () {
  function getCartDrawerElement() {
    return document.querySelector('cart-drawer');
  }

  function replaceSectionFromHTML(sectionHTML, currentSelector, sourceSelector) {
    if (!sectionHTML) return;

    const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const sourceElement = parsedHTML.querySelector(sourceSelector || currentSelector);
    const currentElement = document.querySelector(currentSelector);

    if (sourceElement && currentElement) {
      currentElement.innerHTML = sourceElement.innerHTML;
    }
  }

  function refreshCartDrawer(openAfterRefresh = true) {
    if (!window.routes || !routes.cart_url) return Promise.resolve();

    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => response.json())
      .then((sections) => {
        replaceSectionFromHTML(sections['cart-drawer'], '#CartDrawer');
        replaceSectionFromHTML(sections['cart-icon-bubble'], '#shopify-section-cart-icon-bubble', '.shopify-section');

        const drawer = getCartDrawerElement();

        if (drawer) {
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
          if (typeof drawer.setHeaderCartIconAccessibility === 'function') {
            drawer.setHeaderCartIconAccessibility();
          }
        }

        if (drawer && openAfterRefresh) {
          drawer.classList.remove('is-empty');
          drawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
        }
      })
      .catch((error) => {
        console.error('Cart drawer refresh error:', error);
      });
  }

  window.cartDrawerRefresh = refreshCartDrawer;

  function addToCart(payload, options = {}) {
    const body = typeof payload === 'string' || payload instanceof FormData ? payload : JSON.stringify(payload);
    const isFormData = payload instanceof FormData;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: isFormData
        ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        : { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    })
      .then((response) => {
        if (!response.ok) throw new Error(options.errorMessage || 'Add to cart failed');
        return response.json();
      })
      .then((cartAddResponse) => {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { __pantutiDrawerRefresh: true, cart: cartAddResponse },
          })
        );
        document.dispatchEvent(new CustomEvent('shopify:cart:updated', { bubbles: true, detail: cartAddResponse }));
        return refreshCartDrawer(options.openDrawer !== false).then(() => cartAddResponse);
      });
  }

  window.pantutiCartAdd = addToCart;

  document.addEventListener('cart:refresh', function (event) {
    if (event.detail && event.detail.__pantutiDrawerRefresh) return;
    refreshCartDrawer(false);
  });

  document.addEventListener('change', function (event) {
    const select = event.target.closest('.cart-recommend__select');
    if (!select) return;

    const item = select.closest('.cart-recommend__item');
    const selectedOption = select.options[select.selectedIndex];

    if (!item || !selectedOption) return;

    const image = item.querySelector('.cart-recommend__image');
    const price = item.querySelector('.cart-recommend__price');
    const button = item.querySelector('.cart-recommend__add');

    if (image && selectedOption.dataset.image) {
      image.src = selectedOption.dataset.image;
      image.removeAttribute('srcset');
    }

    if (price && selectedOption.dataset.price) {
      price.textContent = selectedOption.dataset.price;
    }

    if (button) {
      button.dataset.variantId = selectedOption.value;
    }
  });

  document.addEventListener('click', function (event) {
    const button = event.target.closest('.cart-recommend__add');
    if (!button) return;

    event.preventDefault();

    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.classList.add('loading');
    button.disabled = true;

    addToCart(
      {
        id: Number(variantId),
        quantity: 1,
      },
      { errorMessage: 'Add recommendation failed' }
    )
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.disabled = false;
      });
  });
})();
